import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Yii } from "libraries/interfaces/Yii";
import { LabelService } from "../label/label.service";
import { DateService } from "../../date/date.service";
import { DateMomentPipe } from 'libraries/date/dateMoment.pipe';
import { IGetBookingsAirResponse, ISectorAir } from '../resources/booking/interfaces/IGetBookings';
import { BookingType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";

export const BR: string = '\n';
export const HR: string = `_____________________${BR}`;
const DEPARTURE_LABEL: string = Yii.t('wui-shared-js', 'Départ de %from% le %beginDate% à %beginTime%');
const DEPARTURE_DAY_UNKNOWN_LABEL: string = Yii.t('wui-shared-js', 'Départ de %from% Date inconnue');
const ARRIVAL_SAME_DAY_LABEL: string = Yii.t('wui-shared-js', 'Arrivée à %to% à %endTime%');
const ARRIVAL_DAY_AFTER_LABEL: string = Yii.t('wui-shared-js', 'Arrivée à %to% le lendemain à %endTime%');
const ARRIVAL_ANOTHER_DAY_LABEL: string = Yii.t('wui-shared-js', 'Arrivée à %to% le %endDate% à %endTime%');
const ARRIVAL_DAY_UNKNOWN_LABEL: string = Yii.t('wui-shared-js', 'Arrivée à %to% Date inconnue');
const DATE_UNKNOWN_LABEL: string = Yii.t('wui-shared-js', 'Date inconnue');

export enum ESegmentTypes {
    multi = "multi",
    aller = "aller",
    retour = "retour",
    list = "list",
}
@Injectable()
export class PlainTextService {

    public response: any = { data: {}, dictionary: {} };
    //used by moment
    public language: string = sessionStorage.getItem('language');

    constructor(private readonly labelService: LabelService,
        private readonly dateService: DateService,
        private readonly dateMomentPipe: DateMomentPipe) { }

    public generateBookingExport(booking: IGetBookingsAirResponse): string {
        const sectorExports: string[] = booking.sector.map((sector: ISectorAir) => this.generateSectorExport(sector));
        const shortExport: string = booking.sector.map((sector: ISectorAir) => this.generateShortSectorExport(sector)).join(BR);

        return [...sectorExports, shortExport].join(`${BR}${HR}`);
    }

    /***
     * setPlainText
     * Translate a journey objet into a string copyable
     * @journey : journey object
     * @isDictionary : true : use dictionary / false: full, don't use dictionary
     * @segment : "aller", "retour", ou "list"
     ***/
    public setPlainText(journeyArg: any, isDictionary: boolean = false, segmentType: ESegmentTypes = ESegmentTypes.list, index?: number): string {

        let output: string = "";
        let journey: any = journeyArg;

        switch (segmentType) {
            case ESegmentTypes.aller:
                output += HR;
                output += Yii.t('wui-shared-js', 'VOL ALLER') + BR;
                break;
            case ESegmentTypes.retour:
                output += HR;
                output += Yii.t('wui-shared-js', 'VOL RETOUR') + BR;
                break;
            case ESegmentTypes.list:
                journey = { sector: journey };
                break;
            case ESegmentTypes.multi:
                output += HR;
                if(index || index === 0){
                    output += this.labelService.completeLabel(
                        Yii.t('wui-shared-js', 'TRAJET %placeholder%'),
                        (index+1).toString()
                    );
                }else{
                    output += Yii.t('wui-shared-js', 'TRAJET');
                }

                output += BR;
                break;
            default:
                break;
        }
        let sectorIndex: number = 0;

        // Full Text
        (journey.sector || [])
            // keep it comment, I dont understand Why xType dispeared
            .filter((sector: any): boolean => !sector.xType || sector.xType === BookingType.air)
            .forEach((sector: any): void => {
                //get datas from dictionary if needed...
                const infos: { from?: string; to?: string; flight?: string } = {};
                if (isDictionary) {
                    infos.from = buildLocationLabel(this.response.dictionary.airport[sector.from], sector.from);
                    infos.to = buildLocationLabel(this.response.dictionary.airport[sector.to], sector.to);
                    const airCraft: string = sector.aircraft && this.response.dictionary.aircraft[sector.aircraft] ?
                        this.response.dictionary.aircraft[sector.aircraft].name :
                        "";
                    infos.flight = this.labelService.completeLabel(
                        Yii.t('wui-shared-js', 'Vol %carrier% %flight% · %airCraft% · '),
                        {
                            carrier: this.response.dictionary.carrier[sector.carrier.code].name,
                            flight: sector.flight,
                            airCraft
                        }
                    );
                } else {//or take them from data
                    infos.from = buildLocationLabel(sector.from);
                    infos.to = buildLocationLabel(sector.to);
                    infos.flight = this.labelService.completeLabel(
                        Yii.t('wui-shared-js', 'Vol %carrier% %flight% · %airCraft% '),
                        {
                            carrier: sector.carrier ? sector.carrier.name : "",
                            flight: sector.flight,
                            airCraft: sector.aircraft ? `${sector.aircraft.name} · ` : ""
                        }
                    );
                }

                if (sectorIndex > 0 && segmentType !== ESegmentTypes.list) {
                    output += BR;
                    output += this.labelService.completeLabel(
                        Yii.t('wui-shared-js', 'Escale à %placeholder%'),
                        infos.from);
                    output += BR + BR;
                } else if (sectorIndex > 0 && segmentType === ESegmentTypes.list) {
                    output += BR + HR + BR;
                }

                if(!sector.begin){
                    output += this.labelService.completeLabel(
                        DEPARTURE_DAY_UNKNOWN_LABEL,
                        {
                            from: infos.from
                        });
                }else{
                    output += this.labelService.completeLabel(
                        DEPARTURE_LABEL,
                        {
                            from: infos.from,
                            beginDate: this.getDate(sector.begin),
                            beginTime: this.getTime(sector.begin)
                        });
                }

                output += BR;
                if(!sector.begin || !sector.end){
                    output += this.labelService.completeLabel(
                        ARRIVAL_DAY_UNKNOWN_LABEL,
                        {
                            to: infos.to
                        }
                    );
                }
                else{
                    if (this.getDayDiff(sector.begin, sector.end) === 1) {
                        output += this.labelService.completeLabel(
                            ARRIVAL_DAY_AFTER_LABEL,
                            {
                                to: infos.to,
                                endTime: this.getTime(sector.end)
                            }
                        );
                    } else if (this.getDayDiff(sector.begin, sector.end) === 0) {
                        output += this.labelService.completeLabel(
                            ARRIVAL_SAME_DAY_LABEL,
                            {
                                to: infos.to,
                                endTime: this.getTime(sector.end)
                            }
                        );
                    }
                }

                output += BR;

                output += infos.flight;
                if(sector.seat?.length){
                    output += this.labelService.completeLabel(
                        Yii.t('wui-shared-js', 'Classe %placeholder% '),
                        Object.keys(sector.seat)[0]
                    );
                }

                output += BR;

                sectorIndex++;
            });

        return output;
    }

    public setShortPlainText(journey: any, isDictionary: boolean = false): string {

        const sectors = journey.sector || journey;
        return sectors
            .map((sector: any): string => {
                const carrierFlight: string = `${sector.carrier.code}${sector.flight}`;
                const beginDate: string = this.getDate(sector.begin, this.dateService.dateFormats.momentShort);
                const beginTime: string = this.getTime(sector.begin);
                const beginInfo: string = sector.begin ? `${beginTime} ${beginDate}` : DATE_UNKNOWN_LABEL;
                const from: string = isDictionary ?
                    getAirportName(this.response.dictionary.airport[sector.from]) :
                    getAirportName(sector.from);
                const to: string = isDictionary ?
                    getAirportName(this.response.dictionary.airport[sector.to]) :
                    getAirportName(sector.to);
                const durationStop: string = this.generateStopDuration(sector && sector.durationStop && sector.durationStop.minutes);
                const endDate: string = this.getDate(sector.end, this.dateService.dateFormats.momentShort);
                const endTime: string = this.getTime(sector.end);
                const endInfo: string = sector.end ? `${endTime} ${endDate}` : DATE_UNKNOWN_LABEL;
                return `${carrierFlight} ${beginInfo} ${from} - ${to} ${endInfo} ${durationStop}\n`;
            })
            .join("");

    }

    private generateStopDuration(durationInMinutes: number | undefined): string {
        return durationInMinutes
            ? this.labelService.completeLabel(
                Yii.t('wui-shared-js', "durée d'escale %placeholder%"),
                this.dateService.getDuration(durationInMinutes)
            )
            : "";
    }

    private generateSectorExport(sector: ISectorAir): string {
        return [
            this.generateDeparture(sector),
            this.generateArrival(sector),
            this.generateFlight(sector),
        ].join(BR);
    }

    private generateShortSectorExport(sector: ISectorAir): string {
        return [
            `${sector.carrier.code}${sector.flight}`,
            this.getDate(sector.begin, this.dateService.dateFormats.momentShort),
            this.getTime(sector.begin),
            sector.from.airport.name,
            '-',
            sector.to.airport.name,
            this.getDate(sector.end, this.dateService.dateFormats.momentShort),
            this.getTime(sector.end),
            this.generateStopDuration(sector.stop && sector.stop.duration && sector.stop.duration.minute),
        ]
            .filter(Boolean)
            .join(" ");
    }

    private generateDeparture(sector: ISectorAir): string {
        return this.labelService.completeLabel(
            DEPARTURE_LABEL,
            {
                from: buildLocationLabel(sector.from),
                beginDate: this.getDate(sector.begin),
                beginTime: this.getTime(sector.begin)
            });
    }

    private generateArrival(sector: ISectorAir): string {
        if (this.getDayDiff(sector.begin, sector.end) === 1) {
            return this.labelService.completeLabel(
                ARRIVAL_DAY_AFTER_LABEL,
                {
                    to: buildLocationLabel(sector.to),
                    endTime: this.getTime(sector.end)
                }
            );
        } else if (this.getDayDiff(sector.begin, sector.end) === 0) {
            return this.labelService.completeLabel(
                ARRIVAL_SAME_DAY_LABEL,
                {
                    to: buildLocationLabel(sector.to),
                    endTime: this.getTime(sector.end)
                }
            );
        }

        return this.labelService.completeLabel(
            ARRIVAL_ANOTHER_DAY_LABEL,
            {
                to: buildLocationLabel(sector.to),
                endDate: this.getDate(sector.end),
                endTime: this.getTime(sector.end),
            }
        );
    }

    private generateFlight(sector: ISectorAir): string {
        return [
            this.labelService.completeLabel(
                Yii.t('wui-shared-js', 'Vol %carrier% %flight%'),
                {
                    carrier: sector.carrier.name,
                    flight: sector.flight,
                }
            ),
            sector.aircraft && (sector.aircraft.name || sector.aircraft.code),
            sector.seat && sector.seat.cabin && this.labelService.completeLabel(
                Yii.t('wui-shared-js', 'Classe %placeholder% '),
                sector.seat.cabin
            )
        ]
            .filter(Boolean)
            .join(" · ");
    }

    //get Formated Date
    private getDate(date: string, format: string = this.dateService.dateFormats.momentMedium): string {
        return this.dateMomentPipe.transform(date, format);
    }

    //get Formated time
    private getTime(date: string): string {
        return this.dateMomentPipe.transform(date, this.dateService.dateFormats.momentTime);
    }

    //get number of between begin and end
    private getDayDiff(begin: string, end: string): number {
        const beginCleared = this.dateService.removeOffset(begin);
        const endCleared = this.dateService.removeOffset(end);
        const beginDate: number = moment.utc(beginCleared, moment.ISO_8601, this.language).dayOfYear();
        const endDate: number = moment.utc(endCleared, moment.ISO_8601, this.language).dayOfYear();
        return endDate - beginDate;
    }

}

function getAirportName(loc): string {
    return loc && loc.airport ? loc.airport.name : '?';
}

function buildLocationLabel(loc, givenCode?: string): string {
    if (loc && loc.city && loc.airport) {
        return `${loc.city.name} ${loc.airport.name} (${givenCode || loc.airport.code}) `;
    }
    return '?';
}
